// ─────────────────────────────────────────────────────────────────────────────
// What the clinic is called, in front of the people who work there.
//
// "Alba ERPC 2.0" is the CODENAME. It is what the repository, the package, the
// database and this codebase are called, and it is what engineers say. It is
// not what the clinic is called, and it had been appearing in the topbar, the
// sidebar, the browser tab, the PWA manifest, the sign-in screen and the
// sign-off on every notification — six places a member of staff reads, none of
// which should show an internal name.
//
// The English is "Alba Dental Clinic": that is what the brand guideline and
// the letterhead both say, so the interface now agrees with the paper the
// clinic already hands out. The Arabic is "عيادة آلبا", unchanged, because it
// was already the name the clinic uses.
//
// ── ONE PLACE ───────────────────────────────────────────────────────────────
//
// Everything a user reads comes from here. That includes the two files that
// are not TypeScript: `index.html` and the web manifest carry `%BRAND_EN%` and
// `%BRAND_AR%` placeholders which the brand plugin in `vite.config.ts`
// substitutes at serve and build time. Changing the name is one edit to this
// file — never a hunt, which is what it was.
//
// `shared` deliberately, not `web`: the mail templates sign off with it on the
// server, so both halves of the application read the same constant rather than
// keeping a copy each.
// ─────────────────────────────────────────────────────────────────────────────

export const BRAND = {
  /** The full name, as it appears in the topbar, on sign-in and on notifications. */
  en: "Alba Dental Clinic",
  ar: "عيادة آلبا",

  /**
   * The short name, for the home-screen icon and the app switcher, where a
   * long name is truncated by the operating system rather than wrapped.
   * Android allows roughly 12 characters before it elides.
   */
  shortEn: "Alba Clinic",
  shortAr: "عيادة آلبا",

  /**
   * Where staff sign in. The canonical host — `www` 301s here.
   *
   * Here rather than in the template that needs it, for the same reason the
   * name is: it is read by a person, it appears in more than one place over
   * time, and a URL typed by whoever happens to be sending a welcome email is
   * a URL that will one day be the old one. A nurse who bookmarks
   * `alba-erpc.onrender.com` keeps a working link to a host we do not want
   * anyone on, and nobody finds out.
   */
  signInUrl: "https://albaclinic.app",

  /** One line describing the app, for the manifest and the install card. */
  descriptionEn: "HR, payroll, leave and attendance for Alba Dental Clinic.",
  descriptionAr: "الموارد البشرية والرواتب والإجازات والحضور لعيادة آلبا.",
} as const;

/**
 * The codename, kept because a few INTERNAL surfaces should still say it — a
 * generated spreadsheet's author field, the server's own startup line. Nothing
 * a member of staff reads.
 */
export const CODENAME = "Alba ERPC 2.0";
